import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense, useEffect } from "react";
import { CartProvider } from "./context/CartContext";
import { CartDrawer, FloatingCartIcon, CartNotification } from "./components/Cart";

// Critical pages loaded immediately
import Home from "./pages/Home";
import ProductPage from "./pages/ProductPage";
import AboutPage from "./pages/AboutPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import SearchPage from "./pages/SearchPage";
import GiftCardsPage from "./pages/GiftCardsPage";

// Lazy load secondary pages for better performance
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const JewelleryPage = lazy(() => import("./pages/JewelleryPage"));
const EarringsPage = lazy(() => import("./pages/EarringsPage"));
const PendantsPage = lazy(() => import("./pages/PendantsPage"));
const BraceletsPage = lazy(() => import("./pages/BraceletsPage"));
const LapelPinsPage = lazy(() => import("./pages/LapelPinsPage"));
const KeycharmsPage = lazy(() => import("./pages/KeycharmsPage"));
const HairClipsPage = lazy(() => import("./pages/HairClipsPage"));
const HomewaresPage = lazy(() => import("./pages/HomewaresPage"));
const BroochPage = lazy(() => import("./pages/BroochPage"));
const RingsPage = lazy(() => import("./pages/RingsPage"));
const PendantSetPage = lazy(() => import("./pages/PendantSetPage"));
const KeycharmPendantSetPage = lazy(() => import("./pages/KeycharmPendantSetPage"));
const RingHolderPage = lazy(() => import("./pages/RingHolderPage"));
const CharcuterieBoardsPage = lazy(() => import("./pages/CharcuterieBoardsPage"));
const CoasterSetPage = lazy(() => import("./pages/CoasterSetPage"));
const FridgeMagnetPage = lazy(() => import("./pages/FridgeMagnetPage"));
const PaperWeightPage = lazy(() => import("./pages/PaperWeightPage"));
const CandleHolderPage = lazy(() => import("./pages/CandleHolderPage"));
const BottleOpenerPage = lazy(() => import("./pages/BottleOpenerPage"));
const JarsPage = lazy(() => import("./pages/JarsPage"));
const ClocksPage = lazy(() => import("./pages/ClocksPage"));
const PawTrinketPage = lazy(() => import("./pages/PawTrinketPage"));
// Animal Pages
const AnimalsPage = lazy(() => import("./pages/AnimalsPage"));
const FrogsPage = lazy(() => import("./pages/FrogsPage"));
const SnailsPage = lazy(() => import("./pages/SnailsPage"));
const LizardsPage = lazy(() => import("./pages/LizardsPage"));
const BunnyPage = lazy(() => import("./pages/BunnyPage"));
const OctopusPage = lazy(() => import("./pages/OctopusPage"));
const TurtlesPage = lazy(() => import("./pages/TurtlesPage"));
const SeahorsePage = lazy(() => import("./pages/SeahorsePage"));
const StarfishPage = lazy(() => import("./pages/StarfishPage"));
const BirdsPage = lazy(() => import("./pages/BirdsPage"));
const BearsPage = lazy(() => import("./pages/BearsPage"));
const UnicornPage = lazy(() => import("./pages/UnicornPage"));
const CatPage = lazy(() => import("./pages/CatPage"));
const HippoPage = lazy(() => import("./pages/HippoPage"));
// Gifts Pages
const GiftsPage = lazy(() => import("./pages/GiftsPage"));
const PocketBearHugsPage = lazy(() => import("./pages/PocketBearHugsPage"));
const ScullPage = lazy(() => import("./pages/ScullPage"));
const BeadedPensPage = lazy(() => import("./pages/BeadedPensPage"));
const BundleDealsPage = lazy(() => import("./pages/BundleDealsPage"));
// Celebration Pages
const CelebrationsPage = lazy(() => import("./pages/CelebrationsPage"));
const ChristmasPage = lazy(() => import("./pages/ChristmasPage"));
const ValentinesDayPage = lazy(() => import("./pages/ValentinesDayPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const ShippingPage = lazy(() => import("./pages/ShippingPage"));
const LinksPage = lazy(() => import("./pages/LinksPage"));
const TrackOrderPage = lazy(() => import("./pages/TrackOrderPage"));

// Loading spinner component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Redirect component for other category pages
const ShopifyRedirect = ({ collection }) => {
  useEffect(() => {
    window.location.href = `https://gifts.charmingcreationsgifts.com/collections/${collection}`;
  }, [collection]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-amber-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Redirecting to our store...</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/admin" element={<AdminDashboard />} />
                
                {/* Collection Landing Pages - Matching Shopify Navigation */}
                <Route path="/jewellery" element={<JewelleryPage />} />
                <Route path="/earrings" element={<EarringsPage />} />
                <Route path="/pendants" element={<PendantsPage />} />
                <Route path="/bracelets" element={<BraceletsPage />} />
                <Route path="/lapel-pins" element={<LapelPinsPage />} />
                <Route path="/keycharms" element={<KeycharmsPage />} />
                <Route path="/hair-clips" element={<HairClipsPage />} />
                <Route path="/homewares" element={<HomewaresPage />} />
                <Route path="/brooch" element={<BroochPage />} />
                <Route path="/rings" element={<RingsPage />} />
                <Route path="/pendant-set" element={<PendantSetPage />} />
                <Route path="/keycharm-pendant-set" element={<KeycharmPendantSetPage />} />
                <Route path="/ring-holder" element={<RingHolderPage />} />
                <Route path="/charcuterie-boards" element={<CharcuterieBoardsPage />} />
                <Route path="/coaster-set" element={<CoasterSetPage />} />
                <Route path="/fridge-magnet" element={<FridgeMagnetPage />} />
                <Route path="/paper-weight" element={<PaperWeightPage />} />
                <Route path="/candle-holder" element={<CandleHolderPage />} />
                <Route path="/bottle-opener" element={<BottleOpenerPage />} />
                <Route path="/jars" element={<JarsPage />} />
                <Route path="/clocks" element={<ClocksPage />} />
                <Route path="/paw-trinket" element={<PawTrinketPage />} />
                
                {/* Animal Collection Pages */}
                <Route path="/animals" element={<AnimalsPage />} />
                <Route path="/frogs" element={<FrogsPage />} />
                <Route path="/snails" element={<SnailsPage />} />
                <Route path="/lizards" element={<LizardsPage />} />
                <Route path="/bunny" element={<BunnyPage />} />
                <Route path="/octopus" element={<OctopusPage />} />
                <Route path="/turtles" element={<TurtlesPage />} />
                <Route path="/seahorse" element={<SeahorsePage />} />
                <Route path="/starfish" element={<StarfishPage />} />
                <Route path="/birds" element={<BirdsPage />} />
                <Route path="/bears" element={<BearsPage />} />
                <Route path="/unicorn" element={<UnicornPage />} />
                <Route path="/cat" element={<CatPage />} />
                <Route path="/hippo" element={<HippoPage />} />
                
                {/* Gift Collection Pages */}
                <Route path="/gifts" element={<GiftsPage />} />
                <Route path="/pocket-bear-hug" element={<PocketBearHugsPage />} />
                <Route path="/scull" element={<ScullPage />} />
                <Route path="/beaded-pens" element={<BeadedPensPage />} />
                <Route path="/bundle-deals" element={<BundleDealsPage />} />
                
                {/* Celebration Pages */}
                <Route path="/celebrations" element={<CelebrationsPage />} />
                <Route path="/christmas" element={<ChristmasPage />} />
                <Route path="/valentines-day" element={<ValentinesDayPage />} />
                
                {/* Policy Pages */}
                <Route path="/returns" element={<ReturnsPage />} />
                <Route path="/shipping" element={<ShippingPage />} />
                <Route path="/links" element={<LinksPage />} />
                <Route path="/track-order" element={<TrackOrderPage />} />
                
                {/* New Pages - Mirroring Shopify */}
                <Route path="/product/:handle" element={<ProductPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/gift-cards" element={<GiftCardsPage />} />
                
                {/* Redirects for SEO/legacy URLs */}
                <Route path="/catalogue" element={<ShopifyRedirect collection="all" />} />
                <Route path="/collection" element={<ShopifyRedirect collection="all" />} />
                <Route path="/shop" element={<ShopifyRedirect collection="all" />} />
                <Route path="/products" element={<ShopifyRedirect collection="all" />} />
                <Route path="/stationery" element={<ShopifyRedirect collection="beaded-pens" />} />
                
                {/* Catch-all redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            
            {/* Global Cart Components */}
            <FloatingCartIcon />
            <CartDrawer />
            <CartNotification />
          </BrowserRouter>
        </div>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;
